"use client";
import { Badge } from "@components/badge";
import { InputChip } from "@components/input-chip";
import { Menu } from "@components/menu";
import { MenuCheckboxItem } from "@components/menu-checkbox-item";
import { MenuContent } from "@components/menu-content";
import { MenuRadioGroup } from "@components/menu-radio-group";
import { MenuRadioItem } from "@components/menu-radio-item";
import { MenuTrigger } from "@components/menu-trigger";
import { useTranslation } from "@components/theme";
import { Tooltip } from "@components/tooltip";
import AngleDownIcon from "@icons/angle-down";
import AngleUpIcon from "@icons/angle-up";
import CircleCheckIcon from "@icons/circle-check";
import CircleExclamationIcon from "@icons/circle-exclamation";
import CircleInfoIcon from "@icons/circle-info";
import clsx from "clsx";
import React from "react";
import { capitalize } from "../../utils/string";
import type { Slot } from "../text-field/interface";
import textFieldCss from "../text-field/styles.module.css";
import type { SelectProps } from "./interface";
import { selectDefaults } from "./interface";
import css from "./styles.module.css";

const getInputChipSize = (selectSize: SelectProps["size"]): "sm" | "md" => {
  return selectSize === "lg" ? "md" : "sm";
};

/** @ignore */
export const Select = React.forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
  const {
    size = selectDefaults.size,
    validation,
    type = selectDefaults.type,
    open,
    align,
    side,
    disabled,
    startSlot,
    label,
    labelSecondary,
    helperText,
    tooltipText,
    TooltipIcon = CircleInfoIcon,
    options,
    placeholder,
    defaultValue,
    value,
    onValueChange,
    multiple,
    helperTextReserveSpace,
    id,
    name,
    "aria-label": ariaLabel,
  } = props;

  const { t } = useTranslation();

  const onClose = props.multiple ? props.onClose : undefined;

  let chipPropsLocal: React.ComponentProps<typeof InputChip> | undefined;
  let limitChipsLocal: number | undefined;

  if (props.type === "chip" && props.multiple) {
    const { chipProps, limitChips } = props;
    chipPropsLocal = chipProps;
    limitChipsLocal = limitChips;
  }

  const [internalOpen, setInternalOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<string | string[]>(
    defaultValue ?? (multiple || type === "chip" ? [] : ""),
  );

  const selectClassNames = clsx(
    css.root,
    css[`size${capitalize(size)}`],
    validation && css[`validation${capitalize(validation)}`],
    disabled && css.disabled,
  );

  const generatedId = React.useId();
  const selectId = id ?? generatedId;
  const labelId = `${selectId}-label`;
  const isOpen = open ?? internalOpen;
  const selectedValue = value ?? internalValue;
  const previousOpenRef = React.useRef(isOpen);
  const previousValueRef = React.useRef(selectedValue);
  const valueOnOpenRef = React.useRef<string[]>([]);
  const isControlled = value !== undefined;

  React.useEffect(() => {
    if (!multiple || !Array.isArray(selectedValue) || !onClose) {
      return;
    }

    const wasOpen = previousOpenRef.current;
    const prevValue = previousValueRef.current;

    previousOpenRef.current = isOpen;
    previousValueRef.current = selectedValue;

    if (!wasOpen && isOpen) {
      valueOnOpenRef.current = [...selectedValue];
      return;
    }

    if (wasOpen && !isOpen) {
      const hasChanged =
        selectedValue.length !== valueOnOpenRef.current.length ||
        selectedValue.some((v) => !valueOnOpenRef.current.includes(v));

      if (hasChanged) {
        onClose(selectedValue);
      }
      return;
    }

    if (!isOpen && Array.isArray(prevValue)) {
      const hasChanged = selectedValue.length !== prevValue.length || selectedValue.some((v) => !prevValue.includes(v));

      if (hasChanged) {
        onClose(selectedValue);
      }
    }
  }, [isOpen, multiple, onClose, selectedValue]);

  const updateValue = React.useCallback(
    (newValue: string | string[]) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      if (onValueChange) {
        (onValueChange as (value: string | string[]) => void)(newValue);
      }
    },
    [isControlled, onValueChange],
  );

  const selectedOptions = React.useMemo(() => {
    if (multiple && Array.isArray(selectedValue)) {
      return options.filter((option) => selectedValue.includes(option.value));
    }
    if (!multiple && typeof selectedValue === "string") {
      const option = options.find((option) => option.value === selectedValue);
      return option ? [option] : [];
    }
    return [];
  }, [selectedValue, options, multiple]);

  const { visibleChips, hiddenChipsCount } = React.useMemo(() => {
    if (!limitChipsLocal || type !== "chip" || !multiple) {
      return { visibleChips: selectedOptions, hiddenChipsCount: 0 };
    }

    if (selectedOptions.length <= limitChipsLocal) {
      return { visibleChips: selectedOptions, hiddenChipsCount: 0 };
    }

    return {
      visibleChips: selectedOptions.slice(0, limitChipsLocal),
      hiddenChipsCount: selectedOptions.length - limitChipsLocal,
    };
  }, [limitChipsLocal, type, multiple, selectedOptions]);

  const handleSlot = React.useCallback((slot: Slot, size: SelectProps["size"], disabled?: boolean) => {
    if (typeof slot === "string") {
      return <span className={textFieldCss.inputSidetext}>{slot}</span>;
    }

    if (React.isValidElement(slot)) {
      return React.cloneElement(slot, {
        size,
        variant: "neutral",
        disabled,
      });
    }

    return React.createElement(slot, { size });
  }, []);

  const inputDisplayValue = React.useMemo(() => {
    if (type === "chip") {
      return "";
    }

    if (multiple && type === "text") {
      return "";
    }

    return selectedOptions.length > 0 ? selectedOptions[0].label : "";
  }, [type, multiple, selectedOptions]);

  const textFieldClassNames = clsx(
    textFieldCss.root,
    textFieldCss[`size${capitalize(size)}`],
    validation && textFieldCss[`validation${capitalize(validation)}`],
    disabled && textFieldCss.disabled,
  );

  const AngleIcon = isOpen ? AngleUpIcon : AngleDownIcon;

  return (
    <div className={selectClassNames} ref={ref} data-quipu="Select">
      <Menu open={isOpen} onOpenChange={setInternalOpen}>
        <div className={textFieldClassNames}>
          {(label || labelSecondary || tooltipText) && (
            <div className={clsx(textFieldCss.header)}>
              <div className={textFieldCss.headerText}>
                {label && (
                  <label id={labelId} htmlFor={selectId} className={textFieldCss.label}>
                    {label}
                  </label>
                )}
                {labelSecondary && (
                  <span className={textFieldCss.labelOptional} aria-disabled={disabled}>
                    {labelSecondary}
                  </span>
                )}
              </div>
              {tooltipText && (
                <Tooltip label={tooltipText} placement="top">
                  <TooltipIcon size={size} titleAccess={t("info")} />
                </Tooltip>
              )}
            </div>
          )}

          <MenuTrigger asChild data-quipu="SelectTrigger">
            <div
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={`${selectId}-listbox`}
              aria-haspopup="listbox"
              aria-labelledby={label ? labelId : undefined}
              aria-label={!label ? ariaLabel : undefined}
              aria-invalid={validation === "error"}
              tabIndex={disabled ? -1 : 0}
              className={clsx(textFieldCss.inputWrapper, css.inputWrapper)}
              onPointerDown={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest('[data-quipu="InputChip"]')) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              {startSlot && handleSlot(startSlot, size, disabled)}
              <input
                id={selectId}
                name={name}
                aria-labelledby={label ? labelId : undefined}
                aria-describedby={helperText ? `${selectId}-helper` : undefined}
                className={clsx(
                  textFieldCss.input,
                  css.input,
                  multiple && selectedOptions.length > 0 ? "screenreader" : undefined,
                )}
                disabled={disabled}
                placeholder={!inputDisplayValue ? placeholder : undefined}
                value={inputDisplayValue}
                readOnly
                aria-hidden
                tabIndex={-1}
                data-quipu="QuipuSelectInput"
              />
              {multiple && type === "chip" && selectedOptions.length > 0 && (
                <div className={css.inputChipWrapper}>
                  {visibleChips.map((option) => (
                    <InputChip
                      key={option.value}
                      label={option.label}
                      variant="subtle"
                      size={getInputChipSize(size)}
                      {...chipPropsLocal}
                      onDelete={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const newValue = Array.isArray(selectedValue)
                          ? selectedValue.filter((v) => v !== option.value)
                          : [];
                        updateValue(newValue);
                      }}
                    />
                  ))}
                  {hiddenChipsCount > 0 && <span className={css.chipsOptionsAmount}>+{hiddenChipsCount}</span>}
                </div>
              )}
              {multiple && type === "text" && selectedOptions.length > 0 && (
                <span className={css.inputValues}>
                  <span className={css.textSelectedValue}>{selectedOptions[0].label}</span>
                  <span className={css.tagExtraOptionsAmount}>
                    {selectedOptions.length > 1 && (
                      <Badge badgeContent={`+ ${selectedOptions.length - 1}`} size="lg" color="info" />
                    )}
                  </span>
                </span>
              )}
              <AngleIcon size={size} className={css.dropdownIcon} />
            </div>
          </MenuTrigger>

          <div className={textFieldCss.helperText} aria-live={validation ? "polite" : "off"} aria-disabled={disabled}>
            {validation === "error" && <CircleExclamationIcon active />}
            {validation === "success" && <CircleCheckIcon active />}
            {helperText && (
              <span id={`${selectId}-helper`} aria-hidden="true">
                {helperText}
              </span>
            )}
            {helperTextReserveSpace && !helperText && <span style={{ visibility: "hidden" }}>&nbsp;</span>}
          </div>
        </div>
        <MenuContent
          align={align}
          side={side}
          sideOffset={4}
          style={{
            width: "100%",
            maxWidth: "min(20rem, 94vw)",
            minWidth: "var(--radix-dropdown-menu-trigger-width)",
          }}
        >
          {multiple ? (
            options.map((option) => {
              const currentArray = Array.isArray(selectedValue) ? selectedValue : [];
              const isChecked = currentArray.includes(option.value);

              return (
                <MenuCheckboxItem
                  key={option.value}
                  label={option.label}
                  disabled={option.disabled}
                  checked={isChecked}
                  StartIcon={option.StartIcon}
                  onCheckedChange={(checked) => {
                    let newValue: string[];

                    if (checked) {
                      newValue = [...currentArray, option.value];
                    } else {
                      newValue = currentArray.filter((v) => v !== option.value);
                    }

                    updateValue(newValue);
                  }}
                  onSelect={(event) => {
                    event.preventDefault();
                  }}
                />
              );
            })
          ) : (
            <MenuRadioGroup
              value={typeof selectedValue === "string" ? selectedValue : ""}
              onValueChange={(newValue) => {
                setInternalOpen(false);
                updateValue(newValue);
              }}
            >
              {options.map((option) => (
                <MenuRadioItem
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  disabled={option.disabled}
                  StartIcon={option.StartIcon}
                  onSelect={() => {
                    const newValue = option.value;
                    setInternalOpen(false);
                    updateValue(newValue);
                  }}
                />
              ))}
            </MenuRadioGroup>
          )}
        </MenuContent>
      </Menu>
    </div>
  );
});

Select.displayName = "Select";
